// Sprint 4.3 Phase C — Within-branch Warehouse Transfers
// New dedicated page. List + Create form. Cross-branch transfers continue
// to use /stock-transfers (a separate existing module).
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatINR } from "@/lib/api";
import { VoucherScanLog } from "@/components/VoucherScanLog";
import { PageHeader, Card, StatusBadge, EmptyState } from "@/components/ui-helpers";
import { BranchFilter } from "@/components/BranchFilter";
import { Plus, X, Trash2, Search, ArrowRightLeft, Info, Download } from "lucide-react";
import { inputCls, Field } from "./Projects";
import { toast, Toaster } from "sonner";
import { useFormTab } from "@/hooks/useFormTab";
import { useAuth } from "@/contexts/AuthContext";
import { ViewOverlay } from "@/hooks/useViewOverlay";
import { useClientPagination } from "@/hooks/useClientPagination";
import { PaginationControls } from "@/components/PaginationControls";
import { SearchableSelect } from "@/components/SearchableSelect";

export default function WarehouseTransfersPage() {
  const navigate = useNavigate();
  const { hasPerm } = useAuth();
  // Jun-2026 — WT actions honour the dedicated `warehouse_transfer` module
  // OR the umbrella `inventory` module, matching the backend gates.
  const canWT = (a) => hasPerm("warehouse_transfer", a) || hasPerm("inventory", a);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterBranch, setFilterBranch] = useState("");
  const [search, setSearch] = useState("");
  const [viewing, setViewing] = useState(null);
  const [unexec, setUnexec] = useState(null);
  const openFormTab = useFormTab();

  async function load() {
    setLoading(true);
    try {
      const q = filterBranch ? `?branch_id=${filterBranch}` : "";
      const r = await api.get(`/warehouse-transfers${q}`);
      setList(Array.isArray(r.data) ? r.data : (r.data?.items || []));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load transfers");
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterBranch]);

  async function loadAudit() {
    try {
      const r = await api.get("/warehouse-transfers/audit/unexecuted");
      setUnexec(r.data);
    } catch { setUnexec(null); }
  }
  useEffect(() => { if (canWT("edit")) loadAudit(); /* eslint-disable-next-line */ }, []);

  async function executeStock(row) {
    if (!window.confirm(`Execute the pending stock movement for ${row.transfer_number}?\n\nThis will move the material from ${row.from_warehouse_name} to ${row.to_warehouse_name} NOW.`)) return;
    try {
      await api.post(`/warehouse-transfers/${row.id}/execute-stock`);
      toast.success(`${row.transfer_number}: stock movement executed`);
      load(); loadAudit();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Execution failed");
    }
  }

  const openNew = () => openFormTab({
    path: "/warehouse-transfers/new",
    label: "New Warehouse Transfer",
    icon: Plus,
    parent: "/warehouse-transfers",
    onSaved: load,
    render: ({ close, saved, viewList }) => (
      <WarehouseTransferForm onViewList={viewList} onClose={close} onSaved={() => { saved(); load(); }} />
    ),
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter(v => [
      v.transfer_number, v.branch_name, v.from_warehouse_name, v.to_warehouse_name,
      v.status, (v.transfer_date || "").slice(0, 10),
    ].some(x => (x || "").toString().toLowerCase().includes(q)));
  }, [list, search]);

  const { paged, page, pageSize, total, setPage, setPageSize } = useClientPagination(filtered, { initialPageSize: 50 });

  async function cancel(id) {
    if (!confirm("Cancel this Draft transfer?")) return;
    try {
      await api.post(`/warehouse-transfers/${id}/cancel`);
      toast.success("Cancelled"); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    }
  }

  // Sprint 2026-Feb — new: hard-delete a WT.
  // Approved → backend reverses inventory / asset side-effects so the
  // material lands back in the source warehouse's ORIGINAL batch. The
  // confirm() copy tells the user exactly what will happen so there are
  // no surprises. If the backend refuses (dest batch consumed, asset
  // moved on, …) we surface its human-readable detail message.
  async function del(v) {
    const isApproved = v.status === "Approved";
    const msg = isApproved
      ? `Delete transfer ${v.transfer_number || v.id} and RESTORE the material / asset to '${v.from_warehouse_name || "source warehouse"}'?\n\nThis reverses the posted inventory movements. Refused if any line has already been consumed downstream.`
      : `Delete transfer ${v.transfer_number || v.id}? Any pending approval request will also be cleared.`;
    if (!confirm(msg)) return;
    try {
      const r = await api.delete(`/warehouse-transfers/${v.id}`);
      toast.success(
        r?.data?.reversed
          ? "Transfer deleted — material / asset restored to source warehouse."
          : "Transfer deleted."
      );
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  }

  async function downloadPdf(v) {
    try {
      const res = await api.get(`/warehouse-transfers/${v.id}/voucher-pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `warehouse-transfer-${(v.transfer_number || v.id).replace(/[\\/]/g, "-")}.pdf`;
      document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download failed");
    }
  }

  return (
    <div>
      <Toaster richColors position="top-right" />
      <PageHeader
        eyebrow="Stores · Warehouse"
        title="Warehouse Transfer"
        subtitle="Move material / assets between warehouses within the same branch."
        actions={canWT("create") && (
          <button data-testid="new-wt-btn" onClick={openNew}
            className="bg-[#F97316] hover:bg-[#EA580C] text-white px-4 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2">
            <Plus size={14} /> New Transfer
          </button>
        )}
      />

      <div className="mb-3 flex items-center gap-3 flex-wrap">
        <div className="relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="wt-search" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by no / branch / warehouse / status…"
            className="border border-slate-300 bg-white pl-8 pr-3 py-2 text-sm focus:outline-none focus:border-[#F97316] min-w-[320px]" />
        </div>
        <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">Branch</span>
        <BranchFilter value={filterBranch} onChange={setFilterBranch} testid="wt-branch-filter" />
        <div className="ml-auto text-xs text-slate-500">
          <span className="text-slate-400 mr-1">Showing:</span>
          <span className="font-bold text-slate-800 tabular">{filtered.length}</span>
          <span className="text-slate-400 mx-1">/</span>
          <span className="text-slate-600 tabular">{list.length}</span>
        </div>
      </div>

      {/* Jul-2026 repair tool — Approved WTs whose stock movement never ran */}
      {unexec && unexec.count > 0 && (
        <div className="bg-amber-50 border border-amber-300 px-4 py-3 mb-3" data-testid="wt-unexecuted-banner">
          <div className="flex items-center gap-2 mb-2">
            <Info size={15} className="text-amber-700" />
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-amber-800">
              {unexec.count} Approved transfer{unexec.count > 1 ? "s" : ""} never moved stock
            </div>
          </div>
          <div className="text-xs text-amber-900 mb-2">
            These transfers show Approved but their material movement failed during approval. Click Execute to run the movement now — it will validate stock availability first.
          </div>
          <div className="space-y-1.5">
            {unexec.rows.map((r) => (
              <div key={r.id} className="bg-white border border-amber-200 px-3 py-2 flex items-center gap-3 flex-wrap"
                data-testid={`wt-unexec-row-${r.transfer_number}`}>
                <span className="font-mono font-bold text-xs text-slate-800">{r.transfer_number}</span>
                <span className="text-xs text-slate-500">{r.transfer_date}</span>
                <span className="text-xs text-slate-600">{r.from_warehouse_name} → {r.to_warehouse_name}</span>
                <span className="text-[10px] text-amber-700 font-bold uppercase">{r.unexecuted_lines} line{r.unexecuted_lines > 1 ? "s" : ""} pending</span>
                <span className="text-[10px] text-slate-500 truncate max-w-[300px]"
                  title={r.lines.map((l) => `${l.item_name || l.batch_code} × ${l.quantity} ${l.unit}`).join(", ")}>
                  {r.lines.map((l) => `${l.item_name || l.batch_code} × ${l.quantity} ${l.unit}`).join(", ")}
                </span>
                <div className="ml-auto">
                  {r.repairable ? (
                    <button data-testid={`wt-execute-${r.transfer_number}`} onClick={() => executeStock(r)}
                      className="bg-amber-600 hover:bg-amber-700 text-white px-3 py-1.5 text-[10px] uppercase tracking-wider font-bold">
                      Execute Stock Movement
                    </button>
                  ) : (
                    <span className="text-[10px] text-rose-600 font-bold uppercase">Partial state — needs Stock Adjustment</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {list.length === 0 ? (
        <EmptyState title={loading ? "Loading…" : "No warehouse transfers yet"} icon={ArrowRightLeft} />
      ) : filtered.length === 0 ? (
        <EmptyState title="No matches" hint="Clear the search to see all transfers." />
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full" data-testid="wt-table">
            <thead className="bg-slate-50 border-b-2 border-slate-200">
              <tr>{["Transfer No", "Date", "Branch", "From Warehouse", "To Warehouse", "Lines", "Value", "Status", ""].map(h =>
                <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-3 px-4">{h}</th>
              )}</tr>
            </thead>
            <tbody>
              {paged.map(v => (
                <tr key={v.id} className="border-b border-slate-100 hover:bg-slate-50/60">
                  <td className="py-3 px-4 text-xs">
                    <button data-testid={`view-wt-${v.id}`} onClick={() => setViewing(v)}
                      className="font-mono font-bold text-[#F97316] hover:underline">
                      {v.transfer_number || v.id.slice(0, 8)}
                    </button>
                  </td>
                  <td className="py-3 px-4 text-xs text-slate-600">{(v.transfer_date || "").slice(0, 10)}</td>
                  <td className="py-3 px-4 text-xs text-slate-600">{v.branch_name}</td>
                  <td className="py-3 px-4 text-xs text-slate-700 font-bold">{v.from_warehouse_name || "—"}</td>
                  <td className="py-3 px-4 text-xs text-slate-700 font-bold">{v.to_warehouse_name || "—"}</td>
                  <td className="py-3 px-4 text-xs text-slate-600">{(v.line_items || []).length}</td>
                  <td className="py-3 px-4 text-sm tabular font-bold text-[#F97316]">{formatINR(v.total_value || 0)}</td>
                  <td className="py-3 px-4"><StatusBadge status={v.status} /></td>
                  <td className="py-3 px-4 text-right">
                    {canWT("edit") && (v.status === "Draft" || v.status === "Pending") && (
                      <button data-testid={`cancel-wt-${v.id}`} onClick={() => cancel(v.id)}
                        className="text-rose-600 hover:text-rose-800 text-xs mr-3">Cancel</button>
                    )}
                    <button data-testid={`download-wt-pdf-${v.id}`} onClick={() => downloadPdf(v)}
                      title="Download PDF"
                      className="inline-flex items-center gap-1 text-slate-600 hover:text-[#F97316] text-xs mr-3">
                      <Download size={12} /> PDF
                    </button>
                    <button onClick={() => setViewing(v)} className="text-slate-500 hover:text-[#F97316] text-xs mr-3">View</button>
                    {canWT("delete") && (
                    <button
                      data-testid={`delete-wt-${v.id}`}
                      onClick={() => del(v)}
                      title={v.status === "Approved"
                        ? "Delete transfer AND reverse the material / asset back to the source warehouse"
                        : "Delete this transfer"}
                      className="inline-flex items-center gap-1 text-rose-500 hover:text-rose-700 text-xs">
                      <Trash2 size={12} /> Delete
                    </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <PaginationControls page={page} pageSize={pageSize} total={total}
            onPage={setPage} onPageSize={setPageSize}
            pageSizeOptions={[25, 50, 100, 200]} />
        </Card>
      )}

      {viewing && (
        <ViewOverlay testid="wt-view-overlay">
          <div className="px-8 py-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10 shadow-sm">
            <div>
              <h2 className="font-display text-2xl font-bold">{viewing.transfer_number || "Warehouse Transfer"}</h2>
              <p className="text-xs text-slate-500 mt-0.5">{(viewing.transfer_date || "").slice(0, 10)} · {viewing.branch_name}</p>
            </div>
            <button onClick={() => setViewing(null)} className="p-1.5 hover:bg-slate-100"><X size={18} /></button>
          </div>
          <div className="px-8 py-6 space-y-5 max-w-[1600px] mx-auto">
            <div className="flex items-center justify-end gap-2">
              <button data-testid={`download-wt-pdf-overlay-${viewing.id}`}
                onClick={() => downloadPdf(viewing)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#F97316] text-white text-xs font-bold uppercase tracking-wider hover:bg-[#EA580C]">
                <Download size={13} /> Download PDF
              </button>
              {/* Sprint 2026-Feb — Delete affordance from the View overlay,
                  matching the row-level button. Deliberately placed AFTER
                  the PDF download so the destructive action isn't the
                  first primary control the user sees. */}
              {canWT("delete") && (
              <button
                data-testid={`delete-wt-overlay-${viewing.id}`}
                onClick={async () => { await del(viewing); setViewing(null); }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-rose-300 text-rose-700 text-xs font-bold uppercase tracking-wider hover:bg-rose-50">
                <Trash2 size={13} /> Delete
              </button>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div><div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">From Warehouse</div><div className="font-bold text-slate-800">{viewing.from_warehouse_name || "—"}</div></div>
              <div><div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">To Warehouse</div><div className="font-bold text-slate-800">{viewing.to_warehouse_name || "—"}</div></div>
              <div><div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Status</div><StatusBadge status={viewing.status} /></div>
              <div><div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Total Value</div><div className="font-bold text-[#F97316] tabular">{formatINR(viewing.total_value || 0)}</div></div>
            </div>
            <VoucherScanLog vtype="wt" vid={viewing.id} />
            {viewing.remarks && <div className="text-sm text-slate-600 bg-slate-50 px-3 py-2 italic">{viewing.remarks}</div>}
            <Card className="overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>{["#", "Type", "Item / Asset", "Batch / Asset Code", "Qty", "Rate", "Value"].map(h =>
                    <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-2 px-3">{h}</th>
                  )}</tr>
                </thead>
                <tbody>
                  {(viewing.line_items || []).map((li, idx) => (
                    <tr key={idx} className="border-b border-slate-100">
                      <td className="py-2 px-3 text-xs">{idx + 1}</td>
                      <td className="py-2 px-3">
                        <span className={`text-[10px] font-bold uppercase px-2 py-0.5 ${li.line_type === "Material" ? "bg-emerald-50 text-emerald-800" : "bg-blue-50 text-blue-800"}`}>{li.line_type}</span>
                      </td>
                      <td className="py-2 px-3">{li.line_type === "Material" ? li.item_name : <><span className="font-mono text-xs font-bold">{li.asset_code}</span></>}</td>
                      <td className="py-2 px-3 text-[10px] font-mono text-slate-500">{li.batch_code || li.asset_code || "—"}</td>
                      <td className="py-2 px-3 text-xs tabular">{li.line_type === "Material" ? `${li.quantity} ${li.unit || ""}` : "1"}</td>
                      <td className="py-2 px-3 text-xs tabular">{formatINR(li.unit_rate || 0)}</td>
                      <td className="py-2 px-3 text-sm tabular font-bold">{formatINR((li.unit_rate || 0) * (li.quantity || 1))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </div>
        </ViewOverlay>
      )}
    </div>
  );
}

function WarehouseTransferForm({ onClose, onViewList, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [branches, setBranches] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [batches, setBatches] = useState([]);
  const [assets, setAssets] = useState([]);
  const [saving, setSaving] = useState(false);
  // Sprint 2026-Feb — bug fix: after successful save the form used to
  // stay populated. If the user (or a nearby colleague) clicked Save
  // again on the same tab, a DUPLICATE WT was posted with a new
  // voucher number. Factoring the initial state into a helper lets us
  // reset the form immediately after a successful POST so a second
  // click cannot silently re-create the same transfer.
  const emptyForm = () => ({
    transfer_date: today,
    branch_id: "", branch_name: "",
    from_warehouse_id: "", from_warehouse_name: "",
    to_warehouse_id: "",   to_warehouse_name: "",
    remarks: "",
    line_items: [],
  });
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    api.get("/branches").then(r => setBranches(r.data || []));
  }, []);

  useEffect(() => {
    if (!form.branch_id) { setWarehouses([]); setBatches([]); setAssets([]); return; }
    Promise.all([
      api.get(`/warehouses/picker?branch_id=${form.branch_id}`),
      api.get(`/inventory/batches?branch_id=${form.branch_id}&include_depleted=false`),
      api.get("/assets-picker", { params: { branch_id: form.branch_id, status: "Available" } }),
    ]).then(([w, b, a]) => {
      setWarehouses(Array.isArray(w.data) ? w.data : []);
      setBatches(b.data || []);
      // Aug-2026 — server already scopes to Available + this branch
      // (including assets parked in this branch's warehouses whose stamped
      // branch drifted); re-filtering by branch here hid those assets.
      setAssets((a.data || []).filter(x => x.status === "Available"));
    });
  }, [form.branch_id]);

  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const setBranch = (bid) => {
    const br = branches.find(b => b.id === bid);
    setForm(p => ({ ...p,
      branch_id: bid, branch_name: br?.branch_name || "",
      from_warehouse_id: "", from_warehouse_name: "",
      to_warehouse_id:   "", to_warehouse_name:   "",
      line_items: [],
    }));
  };

  function addLine(line_type) {
    setForm(p => ({ ...p, line_items: [...p.line_items, {
      line_type,
      item_id: "", item_code: "", item_name: "", unit: "",
      batch_id: "", batch_code: "", quantity: 0, unit_rate: 0,
      asset_id: "", asset_code: "",
    }]}));
  }
  function updLine(idx, patch) {
    setForm(p => ({ ...p, line_items: p.line_items.map((li, i) => i === idx ? { ...li, ...patch } : li) }));
  }
  function removeLine(idx) {
    setForm(p => ({ ...p, line_items: p.line_items.filter((_, i) => i !== idx) }));
  }
  function pickBatch(idx, batchId) {
    const b = batches.find(x => x.id === batchId);
    if (!b) return;
    updLine(idx, {
      item_id: b.item_id, item_code: b.item_code, item_name: b.item_name, unit: b.unit,
      batch_id: b.id, batch_code: b.batch_code, unit_rate: b.unit_rate,
    });
  }
  function pickAsset(idx, assetId) {
    const a = assets.find(x => x.id === assetId);
    if (!a) return;
    updLine(idx, {
      item_id: a.item_id || "", item_code: a.item_code || "", item_name: a.name, unit: "Nos",
      asset_id: a.id, asset_code: a.asset_code, asset_name: a.name || "", unit_rate: a.purchase_value,
    });
  }

  // From-WH-filtered availability
  const batchesInFromWh = batches.filter(b => !form.from_warehouse_id
                                              || !b.warehouse_id
                                              || b.warehouse_id === form.from_warehouse_id);
  const assetsInFromWh = assets.filter(a => !form.from_warehouse_id
                                            || !a.warehouse_id
                                            || a.warehouse_id === form.from_warehouse_id);

  async function save(e) {
    e.preventDefault();
    if (!form.branch_id) { toast.error("Branch is mandatory"); return; }
    if (!form.from_warehouse_id) { toast.error("From Warehouse is mandatory"); return; }
    if (!form.to_warehouse_id)   { toast.error("To Warehouse is mandatory"); return; }
    if (form.from_warehouse_id === form.to_warehouse_id) {
      toast.error("Source and destination warehouse cannot be the same.");
      return;
    }
    if (form.line_items.length === 0) { toast.error("Add at least one line"); return; }
    for (let i = 0; i < form.line_items.length; i++) {
      const li = form.line_items[i];
      if (li.line_type === "Material") {
        if (!li.batch_id) { toast.error(`Line ${i+1}: pick a batch`); return; }
        if (!li.quantity || Number(li.quantity) <= 0) { toast.error(`Line ${i+1}: quantity must be > 0`); return; }
        const b = batches.find(x => x.id === li.batch_id);
        if (b && Number(li.quantity) > Number(b.remaining_qty) + 1e-6) {
          toast.error(`Line ${i+1}: qty ${li.quantity} exceeds available ${b.remaining_qty} ${b.unit}`); return;
        }
      } else {
        if (!li.asset_id) { toast.error(`Line ${i+1}: pick an asset`); return; }
      }
    }
    setSaving(true);
    try {
      const res = await api.post("/warehouse-transfers", form);
      // Sprint 2026-Feb — post-save reset (bug fix). Announce the new
      // voucher number in the toast so the user has clear feedback
      // that the transfer was accepted, THEN blank out the form so a
      // stray second click cannot re-create a duplicate voucher.
      const num = res?.data?.transfer_number || "";
      toast.success(
        num ? `Transfer ${num} submitted for approval` : "Warehouse transfer submitted for approval.",
        { duration: 4000 },
      );
      onSaved();
      setForm(emptyForm());
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  }

  return (
    <div className="bg-white -mt-6 lg:-mt-10 -mx-4 sm:-mx-6 lg:-mx-10 min-h-[calc(100vh-38px)]" data-testid="wt-form-overlay">
      <Toaster richColors position="top-right" />
      <div className="bg-white w-full min-h-full">
        <div className="px-8 py-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10 shadow-sm">
          <div>
            <h2 className="font-display text-2xl font-bold">New Warehouse Transfer</h2>
            <p className="text-xs text-slate-500 mt-0.5">Move material / assets between warehouses inside the same branch.</p>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" data-testid="wt-view-list-btn" onClick={onViewList}
              className="bg-white border border-slate-300 px-4 py-2.5 text-xs uppercase tracking-wider font-bold text-slate-700 hover:bg-slate-50">
              View List
            </button>
            <button type="button" data-testid="wt-close-btn" onClick={onClose} className="p-1.5 hover:bg-slate-100"><X size={18} /></button>
          </div>
        </div>

        <form onSubmit={save} className="px-8 py-6 space-y-5 max-w-[1600px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <Field label="Transfer Date *">
              <input data-testid="wt-date" type="date" required value={form.transfer_date} onChange={e => setF("transfer_date", e.target.value)} className={inputCls} />
            </Field>
            <Field label="Branch *">
              <select data-testid="wt-branch" required value={form.branch_id} onChange={e => setBranch(e.target.value)} className={inputCls}>
                <option value="">Select branch…</option>
                {branches.map(b => <option key={b.id} value={b.id}>{b.branch_name}</option>)}
              </select>
            </Field>
            <Field label="From Warehouse *">
              <select data-testid="wt-from-warehouse" required disabled={!form.branch_id}
                value={form.from_warehouse_id} onChange={e => {
                  const w = warehouses.find(x => x.id === e.target.value);
                  setForm(p => ({ ...p,
                    from_warehouse_id: e.target.value, from_warehouse_name: w?.warehouse_name || "",
                    line_items: [],
                  }));
                }}
                className={inputCls}
              >
                <option value="">{form.branch_id ? "Select warehouse…" : "Pick a branch first"}</option>
                {warehouses.map(w => (
                  <option key={w.id} value={w.id} disabled={w.id === form.to_warehouse_id}>
                    {w.warehouse_code} — {w.warehouse_name}{w.is_default ? " · Default" : ""}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="To Warehouse *">
              <select data-testid="wt-to-warehouse" required disabled={!form.branch_id}
                value={form.to_warehouse_id} onChange={e => {
                  const w = warehouses.find(x => x.id === e.target.value);
                  setForm(p => ({ ...p,
                    to_warehouse_id: e.target.value, to_warehouse_name: w?.warehouse_name || "",
                  }));
                }}
                className={inputCls}
              >
                <option value="">{form.branch_id ? "Select warehouse…" : "Pick a branch first"}</option>
                {warehouses.map(w => (
                  <option key={w.id} value={w.id} disabled={w.id === form.from_warehouse_id}>
                    {w.warehouse_code} — {w.warehouse_name}{w.is_default ? " · Default" : ""}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Remarks">
              <input data-testid="wt-remarks" value={form.remarks} onChange={e => setF("remarks", e.target.value)} className={inputCls} />
            </Field>
          </div>

          <div className="border-t border-slate-200 pt-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500">Line Items</h3>
              <div className="flex gap-2">
                <button type="button" data-testid="wt-add-material" onClick={() => addLine("Material")}
                  disabled={!form.from_warehouse_id}
                  className="px-3 py-1.5 text-[11px] uppercase tracking-wider font-bold border border-slate-300 hover:bg-slate-50 disabled:opacity-50 flex items-center gap-1">
                  <Plus size={11} /> Material
                </button>
                <button type="button" data-testid="wt-add-asset" onClick={() => addLine("Asset")}
                  disabled={!form.from_warehouse_id}
                  className="px-3 py-1.5 text-[11px] uppercase tracking-wider font-bold border border-slate-300 hover:bg-slate-50 disabled:opacity-50 flex items-center gap-1">
                  <Plus size={11} /> Asset
                </button>
              </div>
            </div>
            {!form.from_warehouse_id && (
              <p className="text-xs text-slate-500 italic flex items-center gap-1"><Info size={12} /> Select branch + From Warehouse first.</p>
            )}

            {form.line_items.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="wt-lines-table">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>{["#", "Type", "Item / Asset", "Qty", "Rate", "Value", ""].map(h =>
                      <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-2 px-2">{h}</th>
                    )}</tr>
                  </thead>
                  <tbody>
                    {form.line_items.map((li, idx) => {
                      const b = batches.find(x => x.id === li.batch_id);
                      const value = li.line_type === "Material"
                        ? Number(li.quantity || 0) * Number(li.unit_rate || 0)
                        : Number(li.unit_rate || 0);
                      return (
                        <tr key={idx} className="border-b border-slate-100" data-testid={`wt-line-${idx}`}>
                          <td className="py-2 px-2 text-xs">{idx + 1}</td>
                          <td className="py-2 px-2">
                            <span className={`text-[10px] font-bold uppercase px-2 py-0.5 ${li.line_type === "Material" ? "bg-emerald-50 text-emerald-800" : "bg-blue-50 text-blue-800"}`}>{li.line_type}</span>
                          </td>
                          <td className="py-2 px-2 min-w-[280px]">
                            {li.line_type === "Material" ? (
                              // Bug-fix 2026-Q1e — was a plain <select> (dropdown-only, no search).
                              // Swapped to SearchableSelect so operators can type-to-search
                              // batches by item name / batch code / unit — critical when a
                              // warehouse holds hundreds of batches.
                              <SearchableSelect
                                value={li.batch_id}
                                onChange={(id) => pickBatch(idx, id)}
                                items={batchesInFromWh}
                                getKey={(r) => r.id}
                                getLabel={(r) => `${r.item_name} · ${r.batch_code}`}
                                getSubLabel={(r) => `${Number(r.remaining_qty || 0)} ${r.unit || ""} available`}
                                getSearchText={(r) => `${r.item_name || ""} ${r.batch_code || ""} ${r.item_code || ""} ${r.unit || ""}`.toLowerCase()}
                                placeholder="Search batch in From Warehouse…"
                                emptyText="No batches in this warehouse"
                                testid={`wt-batch-${idx}`}
                              />
                            ) : (
                              // Bug-fix 2026-Q1e — same fix for assets.
                              <SearchableSelect
                                value={li.asset_id}
                                onChange={(id) => pickAsset(idx, id)}
                                items={assetsInFromWh}
                                getKey={(r) => r.id}
                                getLabel={(r) => `${r.asset_code} · ${r.name}`}
                                getSubLabel={(r) => r.category || r.status || ""}
                                getSearchText={(r) => `${r.asset_code || ""} ${r.name || ""} ${r.category || ""}`.toLowerCase()}
                                placeholder="Search asset in From Warehouse…"
                                emptyText="No available assets in this warehouse"
                                testid={`wt-asset-${idx}`}
                              />
                            )}
                          </td>
                          <td className="py-2 px-2 w-24">
                            {li.line_type === "Material" ? (
                              <input data-testid={`wt-qty-${idx}`} type="number" step="0.01" value={li.quantity}
                                onChange={e => updLine(idx, { quantity: e.target.value })}
                                max={b?.remaining_qty}
                                className="w-full border border-slate-300 px-2 py-1.5 text-sm tabular text-right" />
                            ) : <span className="text-xs text-slate-400">1</span>}
                          </td>
                          <td className="py-2 px-2 text-xs tabular text-slate-600">{formatINR(li.unit_rate || 0)}</td>
                          <td className="py-2 px-2 text-sm tabular font-bold">{formatINR(value)}</td>
                          <td className="py-2 px-2">
                            <button type="button" data-testid={`wt-del-line-${idx}`} onClick={() => removeLine(idx)} className="text-red-500 hover:text-red-700"><Trash2 size={14} /></button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t border-slate-200">
            <button type="button" onClick={onClose} className="px-5 py-2.5 border border-slate-300 text-sm font-bold uppercase tracking-wider">Cancel</button>
            <button data-testid="save-wt-btn" type="submit" disabled={saving} className="px-5 py-2.5 bg-[#F97316] text-white text-sm font-bold uppercase tracking-wider hover:bg-[#EA580C] disabled:opacity-60">
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
